import type {
  AlternateRouteDefinition,
  MapDefinition,
  RampDefinition,
  TrackPoint,
  TracksideEventDefinition,
} from './types.js';

export const GAMEPLAY_ZONE_HALF_LENGTH = 1.5;
export const GAMEPLAY_ZONE_WIDTH_SCALE = 0.82;
export const PRECISION_ZONE_WIDTH_SCALE = 0.48;

export interface TrackProjection {
  x: number;
  y: number;
  z: number;
  distance: number;
  /** Authored control-point segment containing this projection. */
  segment: number;
  segmentT: number;
  /** Segment in the baked road polyline, used only as a fast local hint. */
  bakedSegment: number;
  /** Arc-length progress around the main route. */
  progress: number;
  roadWidth: number;
  /** Tangent heading of the same baked centreline used by the renderer. */
  heading: number;
  /** Normalised three-dimensional tangent of the shared road centreline. */
  tangentX: number;
  tangentY: number;
  tangentZ: number;
  /** Signed crossfall in radians; positive raises the road's right edge. */
  bank: number;
  /** Banked unit vector across the road, pointing to its right edge. */
  rightX: number;
  rightY: number;
  rightZ: number;
  /** Unit road-surface normal shared by physics, rendering and camera. */
  normalX: number;
  normalY: number;
  normalZ: number;
  /** Signed horizontal metres from the centreline; right is positive. */
  signedDistance: number;
  /** Exact height of the triangulated road ribbon below this projection. */
  surfaceY: number;
  /**
   * Signed horizontal distance to the triangulated ribbon boundary.
   * Negative values are inside the visible asphalt, positive values outside.
   */
  roadEdgeDistance: number;
  /** Present when the closest asphalt belongs to an authored alternate route. */
  alternateRouteId?: string;
  alternateRouteProgress?: number;
  alternateRouteFrom?: number;
  alternateRouteTo?: number;
}

export interface BakedRoadSample {
  x: number;
  y: number;
  z: number;
  tangentX: number;
  tangentY: number;
  tangentZ: number;
  bank: number;
  rightX: number;
  rightY: number;
  rightZ: number;
  normalX: number;
  normalY: number;
  normalZ: number;
  width: number;
  progress: number;
  parameter: number;
  sourceSegment: number;
}

export interface BakedRoadGeometry {
  readonly closed: boolean;
  readonly length: number;
  readonly samples: readonly BakedRoadSample[];
  readonly sourceSegmentCount: number;
  readonly segmentIndicesBySource: readonly (readonly number[])[];
  readonly spatialCellSize: number;
  readonly spatialSegments: ReadonlyMap<number, readonly number[]>;
  /** Per-segment X/Z ribbon bounds: minX, maxX, minZ, maxZ. */
  readonly ribbonBounds: Float32Array;
  /** Reused query scratch avoids per-projection Sets and duplicate segment work. */
  readonly spatialMarks: Uint32Array;
  readonly spatialCandidates: number[];
  spatialGeneration: number;
}

export interface BakedTrackGeometry {
  readonly main: BakedRoadGeometry;
  readonly alternateRoutes: ReadonlyMap<string, BakedRoadGeometry>;
  readonly checkpointProgress: readonly number[];
}

export interface GuardrailPoint {
  x: number;
  y: number;
  z: number;
  /** Signed side of the main route: -1 is left, +1 is right. */
  side: -1 | 1;
  progress: number;
}

export interface RampSurfaceProjection {
  definition: RampDefinition;
  x: number;
  y: number;
  z: number;
  heading: number;
  angle: number;
  forward: number;
  lateral: number;
  progress: number;
}

interface DenseSplineSample {
  x: number;
  y: number;
  z: number;
  parameter: number;
  distance: number;
}

interface MutableRoadSample extends BakedRoadSample {
  tangentX: number;
  tangentY: number;
  tangentZ: number;
}

interface SegmentMatch {
  alpha: number;
  distanceSq: number;
  signedDistance: number;
}

interface TriangleSurfaceMatch {
  distanceSq: number;
  surfaceY: number;
  alpha: number;
  lateral: number;
  inside: boolean;
}

interface RibbonSurfaceMatch extends SegmentMatch {
  surfaceY: number;
  edgeDistance: number;
}

interface CachedTrackGeometry {
  track: MapDefinition['track'];
  alternateRoutes: MapDefinition['features']['alternateRoutes'];
  geometry: BakedTrackGeometry;
}

interface ResolvedRampSurface {
  definition: RampDefinition;
  x: number;
  y: number;
  z: number;
  heading: number;
  forwardX: number;
  forwardZ: number;
  rightX: number;
  rightZ: number;
  angle: number;
}

export const ROAD_WIDTH_MULTIPLIER = 2;
export const GUARDRAIL_OFFSET = 1.45;
export const GUARDRAIL_MIN_CLEARANCE = 0.62;
const MAX_CURVE_WIDTH_BONUS = 0.42;
const MAIN_DENSE_DIVISIONS = 3_072;
const ROUTE_DENSE_DIVISIONS = 768;
const ROAD_SPATIAL_QUERY_PADDING = 4;
// Real authored roads top out below this corridor. Physics benchmarks use
// deliberately kilometre-wide asphalt to remove shoulders from the equation;
// indexing that synthetic ribbon in full would create millions of empty grid
// entries even though hinted projections remain close to its centreline.
const MAX_SPATIAL_RIBBON_HALF_WIDTH = 72;

const trackGeometryCache = new WeakMap<MapDefinition, CachedTrackGeometry>();
const rampSurfaceCache = new WeakMap<MapDefinition, { ramps: MapDefinition['features']['ramps']; surfaces: ResolvedRampSurface[] }>();

/**
 * Returns the immutable, deterministic road geometry consumed by both the
 * simulation and Three.js. Samples are equally spaced by arc length, so race
 * progress, visual placement and projection all describe the same road.
 */
export function getTrackGeometry(map: MapDefinition): BakedTrackGeometry {
  const routes = map.features.alternateRoutes;
  const cached = trackGeometryCache.get(map);
  if (cached?.track === map.track && cached.alternateRoutes === routes) return cached.geometry;

  const widths = map.track.map((_point, index) => roadWidthAtTrackPoint(map, index));
  const main = bakeRoad(map.track, widths, true, MAIN_DENSE_DIVISIONS, 640, 1_280);
  const checkpointProgress = map.track.map((_point, index) => (
    parameterToArcProgress(index / map.track.length, main)
  ));
  const alternateRoutes = new Map<string, BakedRoadGeometry>();
  for (const route of routes ?? []) {
    const start = map.track[route.fromTrackIndex];
    const end = map.track[route.toTrackIndex];
    if (!start || !end) continue;
    const points = [start, ...route.points, end];
    const routeWidth = route.width * ROAD_WIDTH_MULTIPLIER;
    alternateRoutes.set(route.id, bakeRoad(
      points,
      points.map(() => routeWidth),
      false,
      ROUTE_DENSE_DIVISIONS,
      96,
      640,
      (samples) => conformRouteToMain(samples, main),
    ));
  }

  const geometry = { main, alternateRoutes, checkpointProgress } satisfies BakedTrackGeometry;
  trackGeometryCache.set(map, { track: map.track, alternateRoutes: routes, geometry });
  return geometry;
}

/** Returns an arc-length pose from an already baked road. */
export function sampleBakedRoad(road: BakedRoadGeometry, progressValue: number): BakedRoadSample {
  const sampleCount = road.samples.length;
  if (sampleCount === 0) return emptyRoadSample();
  if (sampleCount === 1) return { ...road.samples[0]!, progress: 0 };

  const segmentCount = road.closed ? sampleCount : sampleCount - 1;
  const progress = road.closed ? mod(progressValue, 1) : clamp(progressValue, 0, 1);
  const scaled = progress * segmentCount;
  const index = Math.min(segmentCount - 1, Math.floor(scaled));
  const alpha = progress >= 1 ? 1 : scaled - index;
  const nextIndex = road.closed ? (index + 1) % sampleCount : index + 1;
  const first = road.samples[index] ?? road.samples[0]!;
  const second = road.samples[nextIndex] ?? first;
  let tangentX = lerp(first.tangentX, second.tangentX, alpha);
  let tangentY = lerp(first.tangentY, second.tangentY, alpha);
  let tangentZ = lerp(first.tangentZ, second.tangentZ, alpha);
  const tangentLength = Math.hypot(tangentX, tangentY, tangentZ) || 1;
  tangentX /= tangentLength;
  tangentY /= tangentLength;
  tangentZ /= tangentLength;
  const bank = lerp(first.bank, second.bank, alpha);
  const frame = roadFrame(tangentX, tangentY, tangentZ, bank);
  let secondParameter = second.parameter;
  if (road.closed && nextIndex === 0) secondParameter = 1;
  const parameter = road.closed
    ? mod(lerp(first.parameter, secondParameter, alpha), 1)
    : lerp(first.parameter, secondParameter, alpha);
  return {
    x: lerp(first.x, second.x, alpha),
    y: lerp(first.y, second.y, alpha),
    z: lerp(first.z, second.z, alpha),
    tangentX,
    tangentY,
    tangentZ,
    bank,
    rightX: frame.rightX,
    rightY: frame.rightY,
    rightZ: frame.rightZ,
    normalX: frame.normalX,
    normalY: frame.normalY,
    normalZ: frame.normalZ,
    width: lerp(first.width, second.width, alpha),
    progress,
    parameter,
    sourceSegment: sourceSegmentForParameter(parameter, road.sourceSegmentCount, road.closed),
  };
}

export function sampleTrackAt(map: MapDefinition, progress: number): BakedRoadSample {
  return sampleBakedRoad(getTrackGeometry(map).main, progress);
}

/** Pose of an authored checkpoint on the shared spline. */
export function trackPoseAtIndex(map: MapDefinition, index: number): BakedRoadSample {
  const geometry = getTrackGeometry(map);
  const progress = geometry.checkpointProgress[mod(index, map.track.length)] ?? 0;
  return sampleBakedRoad(geometry.main, progress);
}

/** Width of the flush marker and its authoritative trigger. Keeping this in
 * shared geometry prevents a visually valid line from becoming a server miss. */
export function gameplayZoneWidth(
  pose: Pick<BakedRoadSample, 'width'>,
  effect: NonNullable<TracksideEventDefinition['effect']>,
): number {
  return pose.width * (effect === 'precision-zone'
    ? PRECISION_ZONE_WIDTH_SCALE
    : GAMEPLAY_ZONE_WIDTH_SCALE);
}

export function pointInsideGameplayZone(
  pose: BakedRoadSample,
  effect: NonNullable<TracksideEventDefinition['effect']>,
  x: number,
  z: number,
): boolean {
  const offsetX = x - pose.x;
  const offsetZ = z - pose.z;
  const tangentLength = Math.max(0.0001, Math.hypot(pose.tangentX, pose.tangentZ));
  const rightLength = Math.max(0.0001, Math.hypot(pose.rightX, pose.rightZ));
  const along = offsetX * pose.tangentX / tangentLength + offsetZ * pose.tangentZ / tangentLength;
  const across = offsetX * pose.rightX / rightLength + offsetZ * pose.rightZ / rightLength;
  return Math.abs(along) <= GAMEPLAY_ZONE_HALF_LENGTH
    && Math.abs(across) <= gameplayZoneWidth(pose, effect) * 0.5;
}

export function trackProgressAtIndex(map: MapDefinition, index: number): number {
  return getTrackGeometry(map).checkpointProgress[mod(index, map.track.length)] ?? 0;
}

export function trackHeadingAtIndex(map: MapDefinition, index: number): number {
  const pose = trackPoseAtIndex(map, index);
  return Math.atan2(pose.tangentX, pose.tangentZ);
}

export function projectToTrack(map: MapDefinition, x: number, z: number, hintSegment?: number): TrackProjection {
  const road = getTrackGeometry(map).main;
  const segmentCount = road.closed ? road.samples.length : Math.max(0, road.samples.length - 1);
  if (Number.isInteger(hintSegment) && Number(hintSegment) >= 0 && Number(hintSegment) < segmentCount) {
    const hint = Number(hintSegment);
    // The wider window also covers the closest centreline point shifting
    // forward on the inside of a sharp bend, while remaining tiny compared
    // with a full-track search.
    const hinted = projectBakedWindow(road, x, z, hint, 12);
    if (hinted && hinted.distance <= hinted.roadWidth * 0.9) return hinted;
  }

  const nearby = projectNearbySegments(road, x, z);
  if (nearby && nearby.distance <= road.spatialCellSize * 1.5) return nearby;
  return projectAllSegments(road, x, z);
}

/** Height-aware counterpart for self-crossing sections of the main circuit. */
export function projectToTrackAtHeight(
  map: MapDefinition,
  x: number,
  z: number,
  referenceY: number,
  hintSegment?: number,
): TrackProjection {
  if (!Number.isFinite(referenceY)) return projectToTrack(map, x, z, hintSegment);
  const road = getTrackGeometry(map).main;
  let best = Number.isInteger(hintSegment) && Number(hintSegment) >= 0
    ? projectBakedWindowAtHeight(road, x, z, referenceY, Number(hintSegment), 12)
    : null;
  // A valid local ribbon wins while the vehicle remains on or immediately
  // beside it. This continuity guard prevents a wide hairpin from snapping to
  // the neighbouring strip merely because that strip has a closer centreline.
  if (
    best
    && best.roadEdgeDistance <= best.roadWidth * 0.16
    && Math.abs(best.surfaceY - referenceY) <= 2.5
  ) return best;
  const nearby = projectNearbySegmentsAtHeight(road, x, z, referenceY);
  if (!best || (nearby && roadProjectionScore(nearby, referenceY) < roadProjectionScore(best, referenceY))) {
    best = nearby;
  }
  return best ?? projectAllSegmentsAtHeight(road, x, z, referenceY);
}

/** Returns the closest drivable asphalt, including shortcuts and loop routes. */
export function projectToRoad(map: MapDefinition, x: number, z: number, hintSegment?: number): TrackProjection {
  let best = projectToTrack(map, x, z, hintSegment);
  let bestScore = horizontalRoadProjectionScore(best);
  const geometry = getTrackGeometry(map);
  for (const route of map.features.alternateRoutes ?? []) {
    const routeGeometry = geometry.alternateRoutes.get(route.id);
    if (!routeGeometry) continue;
    const nearby = projectNearbySegments(routeGeometry, x, z);
    if (!nearby && best.distance <= routeGeometry.spatialCellSize * 1.5) continue;
    const projected = projectToAlternateRoute(map, route, routeGeometry, x, z, nearby ?? undefined);
    const score = horizontalRoadProjectionScore(projected);
    if (score < bestScore) {
      best = projected;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Returns the closest drivable ribbon at a known world-space surface height.
 * Horizontal distance alone is ambiguous where a shortcut, bridge or looping
 * route passes above another road. The height term keeps a vehicle attached to
 * the surface it is actually driving on without changing legacy callers that
 * do not own a vertical state.
 */
export function projectToRoadAtHeight(
  map: MapDefinition,
  x: number,
  z: number,
  referenceY: number,
  hintSegment?: number,
): TrackProjection {
  if (!Number.isFinite(referenceY)) return projectToRoad(map, x, z, hintSegment);

  const geometry = getTrackGeometry(map);
  let best = projectToTrackAtHeight(map, x, z, referenceY, hintSegment);
  // Kickers stand on the main carriageway. A car riding one is metres above
  // that asphalt, which must not hand it to a shortcut fused alongside.
  if (rampSurfaceAt(map, x, z)) return best;
  let bestScore = roadProjectionScore(best, referenceY);
  for (const route of map.features.alternateRoutes ?? []) {
    const routeGeometry = geometry.alternateRoutes.get(route.id);
    if (!routeGeometry) continue;
    const nearby = projectNearbySegmentsAtHeight(routeGeometry, x, z, referenceY);
    if (!nearby && best.distance <= routeGeometry.spatialCellSize * 1.5) continue;
    const projected = projectToAlternateRoute(
      map,
      route,
      routeGeometry,
      x,
      z,
      nearby ?? projectAllSegmentsAtHeight(routeGeometry, x, z, referenceY),
    );
    const score = roadProjectionScore(projected, referenceY);
    if (score < bestScore) {
      best = projected;
      bestScore = score;
    }
  }
  return best;
}

/** Exact banked deck height represented by a projection. */
export function roadSurfaceHeight(projection: TrackProjection): number {
  return projection.surfaceY;
}

function roadProjectionScore(projection: TrackProjection, referenceY: number): number {
  // Overlapping ribbons at a junction mouth are never perfectly coplanar. A
  // small vertical dead band stops a car sticking to whichever ribbon happens
  // to climb more slowly and lets the centre-line tie-break pick the road it
  // is actually following; flyovers are metres apart and remain separated.
  const verticalDistance = Math.max(0, Math.abs(roadSurfaceHeight(projection) - referenceY) - COPLANAR_RIBBON_TOLERANCE);
  // Vertical separation is intentionally a little stronger than horizontal
  // ribbon distance. A tiny centre-line tie-break keeps overlapping coplanar
  // ribbons deterministic without allowing a neighbouring hairpin to win.
  const outsideDistance = Math.max(0, projection.roadEdgeDistance);
  return outsideDistance * outsideDistance
    + verticalDistance * verticalDistance * 2.25
    + projection.distance * projection.distance * 0.0001;
}

function horizontalRoadProjectionScore(projection: TrackProjection): number {
  const outsideDistance = Math.max(0, projection.roadEdgeDistance);
  return outsideDistance * outsideDistance
    + projection.distance * projection.distance * 0.0001;
}

/** Fast projection for a nearby point known to belong to the same road ribbon
 * as an existing projection. Axle/contact queries are only a few metres from
 * the chassis centre and do not need to search every alternate route again. */
export function projectToRoadNear(
  map: MapDefinition,
  x: number,
  z: number,
  reference: TrackProjection,
): TrackProjection {
  const geometry = getTrackGeometry(map);
  if (reference.alternateRouteId) {
    const route = (map.features.alternateRoutes ?? []).find((candidate) => (
      candidate.id === reference.alternateRouteId
    ));
    const road = geometry.alternateRoutes.get(reference.alternateRouteId);
    if (route && road) {
      const nearby = projectBakedWindow(road, x, z, reference.bakedSegment, 12);
      if (nearby) return projectToAlternateRoute(map, route, road, x, z, nearby);
    }
  } else {
    const nearby = projectBakedWindow(geometry.main, x, z, reference.bakedSegment, 12);
    if (nearby) return nearby;
  }
  return projectToRoad(map, x, z, reference.bakedSegment);
}

/** Returns the authored ramp deck under a world-space point, if any. The same
 * linear surface is used for grounding, chassis pitch and take-off detection,
 * so cars climb the visible ramp instead of being launched from its base. */
export function rampSurfaceAt(map: MapDefinition, x: number, z: number): RampSurfaceProjection | null {
  let best: RampSurfaceProjection | null = null;
  for (const surface of resolvedRampSurfaces(map)) {
    const dx = x - surface.x;
    const dz = z - surface.z;
    const forward = dx * surface.forwardX + dz * surface.forwardZ;
    if (forward < 0 || forward > surface.definition.length) continue;
    const lateral = dx * surface.rightX + dz * surface.rightZ;
    if (Math.abs(lateral) > surface.definition.width * 0.5) continue;
    const progress = clamp(forward / Math.max(0.1, surface.definition.length), 0, 1);
    const candidate: RampSurfaceProjection = {
      definition: surface.definition,
      x: surface.x,
      y: surface.y + surface.definition.height * progress,
      z: surface.z,
      heading: surface.heading,
      angle: surface.angle,
      forward,
      lateral,
      progress,
    };
    if (!best || candidate.y > best.y) best = candidate;
  }
  return best;
}

/**
 * World-space driving surface beneath a vehicle. Asphalt keeps the exact road
 * elevation, while off-road ground eases back toward the map datum. Lakes are
 * treated as low ground unless a real road or bridge crosses them.
 */
export function groundHeightAt(
  map: MapDefinition,
  x: number,
  z: number,
  projectionValue?: TrackProjection,
): number {
  const projection = projectionValue ?? projectToRoad(map, x, z);
  const ramp = rampSurfaceAt(map, x, z);
  // A twisting two-triangle quad can differ from its mathematical centreline
  // by a couple of millimetres. Pin only that tiny central strip to the
  // authored line to avoid suspension jitter; the rest follows the exact
  // visible triangle surface.
  const bankedRoadY = Math.abs(projection.signedDistance) < 0.02
    ? projection.y
    : roadSurfaceHeight(projection);
  if (ramp) return Math.max(bankedRoadY, ramp.y);
  if (projection.roadEdgeDistance <= projection.roadWidth * 0.12) return bankedRoadY;
  if (pointInsideLake(map, x, z, 1.03)) return 0;
  const plateauBeyondEdge = projection.roadWidth * 0.08 + 5;
  const transition = clamp((projection.roadEdgeDistance - plateauBeyondEdge) / 54, 0, 1);
  const smoothTransition = transition * transition * (3 - 2 * transition);
  return bankedRoadY * (1 - smoothTransition);
}

function resolvedRampSurfaces(map: MapDefinition): ResolvedRampSurface[] {
  const ramps = map.features.ramps;
  const cached = rampSurfaceCache.get(map);
  if (cached?.ramps === ramps) return cached.surfaces;
  const surfaces = ramps.map((definition) => {
    const pose = trackPoseAtIndex(map, definition.trackIndex);
    const heading = Math.atan2(pose.tangentX, pose.tangentZ);
    return {
      definition,
      x: pose.x,
      y: pose.y,
      z: pose.z,
      heading,
      forwardX: Math.sin(heading),
      forwardZ: Math.cos(heading),
      rightX: Math.cos(heading),
      rightZ: -Math.sin(heading),
      angle: Math.atan2(definition.height, Math.max(0.1, definition.length)),
    };
  });
  rampSurfaceCache.set(map, { ramps, surfaces });
  return surfaces;
}

export function pointInsideLake(map: MapDefinition, x: number, z: number, scale = 1): boolean {
  return (map.features.lakes ?? []).some((lake) => {
    const rotation = -(lake.rotation ?? 0);
    const offsetX = x - lake.x;
    const offsetZ = z - lake.z;
    const localX = offsetX * Math.cos(rotation) - offsetZ * Math.sin(rotation);
    const localZ = offsetX * Math.sin(rotation) + offsetZ * Math.cos(rotation);
    return Math.hypot(localX / (lake.radiusX * scale), localZ / (lake.radiusZ * scale)) <= 1;
  });
}

function projectToAlternateRoute(
  map: MapDefinition,
  route: AlternateRouteDefinition,
  road: BakedRoadGeometry,
  x: number,
  z: number,
  nearby?: TrackProjection,
): TrackProjection {
  let best = nearby ?? initialProjection(road);
  if (!nearby) {
    best = projectAllSegments(road, x, z);
  }

  const routeProgress = best.progress;
  const trackGeometry = getTrackGeometry(map);
  const fromProgress = trackGeometry.checkpointProgress[route.fromTrackIndex] ?? 0;
  const toProgress = trackGeometry.checkpointProgress[route.toTrackIndex] ?? 0;
  const routeSpanProgress = mod(toProgress - fromProgress, 1);
  const mainProgress = mod(fromProgress + routeSpanProgress * routeProgress, 1);
  const indexSpan = mod(route.toTrackIndex - route.fromTrackIndex, map.track.length);
  const sourcePosition = route.fromTrackIndex + indexSpan * routeProgress;
  return {
    ...best,
    segment: mod(Math.floor(sourcePosition), map.track.length),
    segmentT: sourcePosition - Math.floor(sourcePosition),
    progress: mainProgress,
    alternateRouteId: route.id,
    alternateRouteProgress: routeProgress,
    alternateRouteFrom: route.fromTrackIndex,
    alternateRouteTo: route.toTrackIndex,
  };
}

function projectNearbySegments(
  road: BakedRoadGeometry,
  x: number,
  z: number,
): TrackProjection | null {
  let bestIndex = -1;
  let bestScore = Number.POSITIVE_INFINITY;
  const match = emptyRibbonSurfaceMatch();
  const triangle = emptyTriangleSurfaceMatch();
  for (const segment of nearbyBakedSegments(road, x, z)) {
    bakedSegmentSurfaceMatch(road, segment, x, z, match, triangle);
    const score = horizontalRibbonMatchScore(match);
    if (score >= bestScore) continue;
    bestIndex = segment;
    bestScore = score;
  }
  return bestIndex < 0
    ? null
    : exactProjectionFromBakedSegment(road, bestIndex, x, z);
}

function projectNearbySegmentsAtHeight(
  road: BakedRoadGeometry,
  x: number,
  z: number,
  referenceY: number,
): TrackProjection | null {
  let bestIndex = -1;
  let bestAlpha = 0;
  let bestDistanceSq = Number.POSITIVE_INFINITY;
  let bestSignedDistance = 0;
  let bestSurfaceY = 0;
  let bestEdgeDistance = Number.POSITIVE_INFINITY;
  let bestScore = Number.POSITIVE_INFINITY;
  const match: RibbonSurfaceMatch = {
    alpha: 0,
    distanceSq: Number.POSITIVE_INFINITY,
    signedDistance: 0,
    surfaceY: 0,
    edgeDistance: Number.POSITIVE_INFINITY,
  };
  const triangle: TriangleSurfaceMatch = {
    distanceSq: Number.POSITIVE_INFINITY,
    surfaceY: 0,
    alpha: 0,
    lateral: 0,
    inside: false,
  };
  for (const segment of nearbyBakedSegments(road, x, z)) {
    bakedSegmentSurfaceMatch(road, segment, x, z, match, triangle);
    const verticalDistance = match.surfaceY - referenceY;
    const outsideDistance = Math.max(0, match.edgeDistance);
    const score = outsideDistance * outsideDistance
      + verticalDistance * verticalDistance * 2.25
      + match.distanceSq * 0.0001;
    if (score >= bestScore) continue;
    bestIndex = segment;
    bestAlpha = match.alpha;
    bestDistanceSq = match.distanceSq;
    bestSignedDistance = match.signedDistance;
    bestSurfaceY = match.surfaceY;
    bestEdgeDistance = match.edgeDistance;
    bestScore = score;
  }
  return bestIndex < 0
    ? null
    : projectionFromBakedSegment(
        road,
        bestIndex,
        bestAlpha,
        bestDistanceSq,
        bestSignedDistance,
        bestSurfaceY,
        bestEdgeDistance,
      );
}

function projectBakedWindow(
  road: BakedRoadGeometry,
  x: number,
  z: number,
  centre: number,
  radius: number,
): TrackProjection | null {
  const segmentCount = road.closed ? road.samples.length : Math.max(0, road.samples.length - 1);
  if (segmentCount === 0) return null;
  let bestIndex = -1;
  let bestOffset = radius + 1;
  let bestScore = Number.POSITIVE_INFINITY;
  const match = emptyRibbonSurfaceMatch();
  const triangle = emptyTriangleSurfaceMatch();
  const seedOffset = closestWindowBoundsOffset(road, segmentCount, x, z, centre, radius);
  // Pass `radius + 1` is the seed; the ordered sweep then keeps the earliest
  // offset among equal scores, matching the unpruned window exactly.
  for (let pass = -radius - 1; pass <= radius; pass += 1) {
    const offset = pass < -radius ? seedOffset : pass;
    if (pass >= -radius && offset === seedOffset) continue;
    const index = windowSegmentIndex(road, segmentCount, centre + offset);
    if (pass >= -radius && ribbonScoreLowerBound(road, index, x, z) > bestScore) continue;
    bakedSegmentSurfaceMatch(road, index, x, z, match, triangle);
    const score = horizontalRibbonMatchScore(match);
    if (score > bestScore || (score === bestScore && offset > bestOffset)) continue;
    bestIndex = index;
    bestOffset = offset;
    bestScore = score;
  }
  return bestIndex < 0
    ? null
    : exactProjectionFromBakedSegment(road, bestIndex, x, z);
}

function projectBakedWindowAtHeight(
  road: BakedRoadGeometry,
  x: number,
  z: number,
  referenceY: number,
  centre: number,
  radius: number,
): TrackProjection | null {
  const segmentCount = road.closed ? road.samples.length : Math.max(0, road.samples.length - 1);
  if (segmentCount === 0) return null;
  let bestIndex = -1;
  let bestAlpha = 0;
  let bestDistanceSq = Number.POSITIVE_INFINITY;
  let bestSignedDistance = 0;
  let bestSurfaceY = 0;
  let bestEdgeDistance = Number.POSITIVE_INFINITY;
  let bestScore = Number.POSITIVE_INFINITY;
  const match: RibbonSurfaceMatch = {
    alpha: 0,
    distanceSq: Number.POSITIVE_INFINITY,
    signedDistance: 0,
    surfaceY: 0,
    edgeDistance: Number.POSITIVE_INFINITY,
  };
  const triangle: TriangleSurfaceMatch = {
    distanceSq: Number.POSITIVE_INFINITY,
    surfaceY: 0,
    alpha: 0,
    lateral: 0,
    inside: false,
  };
  let bestOffset = radius + 1;
  const seedOffset = closestWindowBoundsOffset(road, segmentCount, x, z, centre, radius);
  for (let pass = -radius - 1; pass <= radius; pass += 1) {
    const offset = pass < -radius ? seedOffset : pass;
    if (pass >= -radius && offset === seedOffset) continue;
    const index = windowSegmentIndex(road, segmentCount, centre + offset);
    if (pass >= -radius && ribbonScoreLowerBound(road, index, x, z) > bestScore) continue;
    bakedSegmentSurfaceMatch(road, index, x, z, match, triangle);
    const verticalDistance = match.surfaceY - referenceY;
    const outsideDistance = Math.max(0, match.edgeDistance);
    const score = outsideDistance * outsideDistance
      + verticalDistance * verticalDistance * 2.25
      + match.distanceSq * 0.0001;
    if (score > bestScore || (score === bestScore && offset > bestOffset)) continue;
    bestIndex = index;
    bestOffset = offset;
    bestAlpha = match.alpha;
    bestDistanceSq = match.distanceSq;
    bestSignedDistance = match.signedDistance;
    bestSurfaceY = match.surfaceY;
    bestEdgeDistance = match.edgeDistance;
    bestScore = score;
  }
  return bestIndex < 0
    ? null
    : projectionFromBakedSegment(
        road,
        bestIndex,
        bestAlpha,
        bestDistanceSq,
        bestSignedDistance,
        bestSurfaceY,
        bestEdgeDistance,
      );
}

function projectAllSegments(road: BakedRoadGeometry, x: number, z: number): TrackProjection {
  const bestIndex = scanAllSegments(road, x, z, Number.NaN);
  return bestIndex < 0
    ? initialProjection(road)
    : exactProjectionFromBakedSegment(road, bestIndex, x, z);
}

/** Float32 bounds may round inward by a few micrometres on large maps. */
const RIBBON_BOUNDS_SLACK = 0.01;

/**
 * Exact lower bound for every ribbon score used by the full-road scans.
 * The visible deck and its centreline both lie inside the segment's X/Z
 * bounds, so the bound distance never exceeds either the outside-edge or the
 * centreline distance, and the vertical score term is never negative. A
 * segment whose bound already exceeds the best score cannot win (scans keep
 * the first strict minimum), which lets an off-road query skip almost every
 * triangle test while returning bit-identical projections.
 */
function ribbonScoreLowerBound(road: BakedRoadGeometry, index: number, x: number, z: number): number {
  return boundsScoreLowerBound(road.ribbonBounds, index, x, z);
}

function windowSegmentIndex(road: BakedRoadGeometry, segmentCount: number, position: number): number {
  return road.closed ? mod(position, segmentCount) : clamp(position, 0, segmentCount - 1);
}

function closestWindowBoundsOffset(
  road: BakedRoadGeometry,
  segmentCount: number,
  x: number,
  z: number,
  centre: number,
  radius: number,
): number {
  let closest = 0;
  let closestBound = Number.POSITIVE_INFINITY;
  for (let offset = -radius; offset <= radius; offset += 1) {
    const bound = ribbonScoreLowerBound(road, windowSegmentIndex(road, segmentCount, centre + offset), x, z);
    if (bound >= closestBound) continue;
    closest = offset;
    closestBound = bound;
  }
  return closest;
}

/** Consecutive baked segments are spatially coherent, so a two-level bound
 * hierarchy over fixed runs of them rejects most of a ribbon per test. */
const SEGMENT_CHUNK_SIZE = 32;
const chunkBoundsByRoad = new WeakMap<BakedRoadGeometry, Float32Array>();

function segmentChunkBounds(road: BakedRoadGeometry, segmentCount: number): Float32Array {
  const cached = chunkBoundsByRoad.get(road);
  if (cached) return cached;
  const chunkCount = Math.ceil(segmentCount / SEGMENT_CHUNK_SIZE);
  const bounds = new Float32Array(chunkCount * 4);
  for (let chunk = 0; chunk < chunkCount; chunk += 1) {
    let minimumX = Number.POSITIVE_INFINITY;
    let maximumX = Number.NEGATIVE_INFINITY;
    let minimumZ = Number.POSITIVE_INFINITY;
    let maximumZ = Number.NEGATIVE_INFINITY;
    const last = Math.min(segmentCount, (chunk + 1) * SEGMENT_CHUNK_SIZE);
    for (let index = chunk * SEGMENT_CHUNK_SIZE; index < last; index += 1) {
      minimumX = Math.min(minimumX, road.ribbonBounds[index * 4]!);
      maximumX = Math.max(maximumX, road.ribbonBounds[index * 4 + 1]!);
      minimumZ = Math.min(minimumZ, road.ribbonBounds[index * 4 + 2]!);
      maximumZ = Math.max(maximumZ, road.ribbonBounds[index * 4 + 3]!);
    }
    bounds[chunk * 4] = minimumX;
    bounds[chunk * 4 + 1] = maximumX;
    bounds[chunk * 4 + 2] = minimumZ;
    bounds[chunk * 4 + 3] = maximumZ;
  }
  chunkBoundsByRoad.set(road, bounds);
  return bounds;
}

function boundsScoreLowerBound(bounds: Float32Array, index: number, x: number, z: number): number {
  const offset = index * 4;
  const dx = Math.max(bounds[offset]! - RIBBON_BOUNDS_SLACK - x, 0, x - bounds[offset + 1]! - RIBBON_BOUNDS_SLACK);
  const dz = Math.max(bounds[offset + 2]! - RIBBON_BOUNDS_SLACK - z, 0, z - bounds[offset + 3]! - RIBBON_BOUNDS_SLACK);
  return (dx * dx + dz * dz) * (1 + 0.0001);
}

/**
 * Exact whole-ribbon search. Returns the same segment as an ascending scan
 * that keeps the first strict minimum of the ribbon score (horizontal, or
 * height-aware when `referenceY` is finite), but visits only chunks and
 * segments whose bounds could still beat the best score found so far.
 */
function scanAllSegments(road: BakedRoadGeometry, x: number, z: number, referenceY: number): number {
  const segmentCount = road.closed ? road.samples.length : Math.max(0, road.samples.length - 1);
  if (segmentCount === 0) return -1;
  const chunkBounds = segmentChunkBounds(road, segmentCount);
  const chunkCount = chunkBounds.length / 4;
  const match = emptyRibbonSurfaceMatch();
  const triangle = emptyTriangleSurfaceMatch();
  const heightAware = Number.isFinite(referenceY);
  const scoreSegment = (index: number): number => {
    bakedSegmentSurfaceMatch(road, index, x, z, match, triangle);
    if (!heightAware) return horizontalRibbonMatchScore(match);
    const verticalDistance = match.surfaceY - referenceY;
    const outsideDistance = Math.max(0, match.edgeDistance);
    return outsideDistance * outsideDistance
      + verticalDistance * verticalDistance * 2.25
      + match.distanceSq * 0.0001;
  };

  // Seed with the segment of tightest bounds inside the nearest chunk.
  let seedChunk = 0;
  let seedChunkBound = Number.POSITIVE_INFINITY;
  for (let chunk = 0; chunk < chunkCount; chunk += 1) {
    const bound = boundsScoreLowerBound(chunkBounds, chunk, x, z);
    if (bound >= seedChunkBound) continue;
    seedChunk = chunk;
    seedChunkBound = bound;
  }
  let bestIndex = seedChunk * SEGMENT_CHUNK_SIZE;
  let seedBound = Number.POSITIVE_INFINITY;
  const seedLast = Math.min(segmentCount, (seedChunk + 1) * SEGMENT_CHUNK_SIZE);
  for (let index = seedChunk * SEGMENT_CHUNK_SIZE; index < seedLast; index += 1) {
    const bound = boundsScoreLowerBound(road.ribbonBounds, index, x, z);
    if (bound >= seedBound) continue;
    bestIndex = index;
    seedBound = bound;
  }
  const seed = bestIndex;
  let bestScore = scoreSegment(seed);

  for (let chunk = 0; chunk < chunkCount; chunk += 1) {
    if (boundsScoreLowerBound(chunkBounds, chunk, x, z) > bestScore) continue;
    const last = Math.min(segmentCount, (chunk + 1) * SEGMENT_CHUNK_SIZE);
    for (let index = chunk * SEGMENT_CHUNK_SIZE; index < last; index += 1) {
      if (index === seed || boundsScoreLowerBound(road.ribbonBounds, index, x, z) > bestScore) continue;
      const score = scoreSegment(index);
      if (score > bestScore || (score === bestScore && index > bestIndex)) continue;
      bestIndex = index;
      bestScore = score;
    }
  }
  return bestIndex;
}

function emptyRibbonSurfaceMatch(): RibbonSurfaceMatch {
  return {
    alpha: 0,
    distanceSq: Number.POSITIVE_INFINITY,
    signedDistance: 0,
    surfaceY: 0,
    edgeDistance: Number.POSITIVE_INFINITY,
  };
}

function emptyTriangleSurfaceMatch(): TriangleSurfaceMatch {
  return {
    distanceSq: Number.POSITIVE_INFINITY,
    surfaceY: 0,
    alpha: 0,
    lateral: 0,
    inside: false,
  };
}

function horizontalRibbonMatchScore(match: RibbonSurfaceMatch): number {
  const outsideDistance = Math.max(0, match.edgeDistance);
  return outsideDistance * outsideDistance + match.distanceSq * 0.0001;
}

function projectAllSegmentsAtHeight(
  road: BakedRoadGeometry,
  x: number,
  z: number,
  referenceY: number,
): TrackProjection {
  const bestIndex = scanAllSegments(road, x, z, referenceY);
  if (bestIndex < 0) return initialProjection(road);
  const match = emptyRibbonSurfaceMatch();
  bakedSegmentSurfaceMatch(road, bestIndex, x, z, match, emptyTriangleSurfaceMatch());
  return projectionFromBakedSegment(
    road,
    bestIndex,
    match.alpha,
    match.distanceSq,
    match.signedDistance,
    match.surfaceY,
    match.edgeDistance,
  );
}

function bakedSegmentDistance(
  road: BakedRoadGeometry,
  segmentIndex: number,
  x: number,
  z: number,
  result: SegmentMatch,
): void {
  const first = road.samples[segmentIndex] ?? road.samples[0] ?? emptyRoadSample();
  const nextIndex = road.closed ? (segmentIndex + 1) % road.samples.length : segmentIndex + 1;
  const second = road.samples[nextIndex] ?? first;
  const dx = second.x - first.x;
  const dz = second.z - first.z;
  const lengthSq = dx * dx + dz * dz;
  const alpha = lengthSq <= 1e-10
    ? 0
    : clamp(((x - first.x) * dx + (z - first.z) * dz) / lengthSq, 0, 1);
  const px = first.x + dx * alpha;
  const pz = first.z + dz * alpha;
  result.alpha = alpha;
  result.distanceSq = (x - px) ** 2 + (z - pz) ** 2;
  let rightX = dz;
  let rightZ = -dx;
  let rightLength = Math.hypot(rightX, rightZ);
  if (rightLength <= 1e-9) {
    rightX = first.tangentZ;
    rightZ = -first.tangentX;
    rightLength = Math.hypot(rightX, rightZ) || 1;
  }
  result.signedDistance = (x - px) * rightX / rightLength + (z - pz) * rightZ / rightLength;
}

/**
 * Projects an X/Z point onto the same two triangles used by the rendered road
 * ribbon. Centre-line distance remains available for handling and progress,
 * while surface height and edge clearance come from the actual visible deck.
 */
function bakedSegmentSurfaceMatch(
  road: BakedRoadGeometry,
  segmentIndex: number,
  x: number,
  z: number,
  result: RibbonSurfaceMatch,
  triangle: TriangleSurfaceMatch,
): void {
  const first = road.samples[segmentIndex] ?? road.samples[0] ?? emptyRoadSample();
  const nextIndex = road.closed ? (segmentIndex + 1) % road.samples.length : segmentIndex + 1;
  const second = road.samples[nextIndex] ?? first;
  const firstHalfWidth = first.width * 0.5;
  const secondHalfWidth = second.width * 0.5;
  const left0X = first.x - first.rightX * firstHalfWidth;
  const left0Y = first.y - first.rightY * firstHalfWidth;
  const left0Z = first.z - first.rightZ * firstHalfWidth;
  const right0X = first.x + first.rightX * firstHalfWidth;
  const right0Y = first.y + first.rightY * firstHalfWidth;
  const right0Z = first.z + first.rightZ * firstHalfWidth;
  const left1X = second.x - second.rightX * secondHalfWidth;
  const left1Y = second.y - second.rightY * secondHalfWidth;
  const left1Z = second.z - second.rightZ * secondHalfWidth;
  const right1X = second.x + second.rightX * secondHalfWidth;
  const right1Y = second.y + second.rightY * secondHalfWidth;
  const right1Z = second.z + second.rightZ * secondHalfWidth;

  triangle.distanceSq = Number.POSITIVE_INFINITY;
  triangle.surfaceY = first.y;
  triangle.alpha = 0;
  triangle.lateral = 0;
  triangle.inside = false;
  matchSurfaceTriangle(
    x,
    z,
    left0X,
    left0Y,
    left0Z,
    0,
    -1,
    left1X,
    left1Y,
    left1Z,
    1,
    -1,
    right0X,
    right0Y,
    right0Z,
    0,
    1,
    triangle,
  );
  matchSurfaceTriangle(
    x,
    z,
    left1X,
    left1Y,
    left1Z,
    1,
    -1,
    right1X,
    right1Y,
    right1Z,
    1,
    1,
    right0X,
    right0Y,
    right0Z,
    0,
    1,
    triangle,
  );

  // Keep progress and `distance` tied to the baked centreline. Triangle
  // barycentrics describe the visible deck perfectly, but their longitudinal
  // coordinate shifts slightly across the quad diagonal and would make a
  // centreline point report a non-zero road distance.
  const centreDeltaX = second.x - first.x;
  const centreDeltaZ = second.z - first.z;
  const centreLengthSq = centreDeltaX * centreDeltaX + centreDeltaZ * centreDeltaZ;
  const centreAlpha = centreLengthSq <= 1e-10
    ? 0
    : clamp(
        ((x - first.x) * centreDeltaX + (z - first.z) * centreDeltaZ) / centreLengthSq,
        0,
        1,
      );
  const centreX = lerp(first.x, second.x, centreAlpha);
  const centreZ = lerp(first.z, second.z, centreAlpha);
  let rightX = lerp(first.rightX, second.rightX, centreAlpha);
  let rightZ = lerp(first.rightZ, second.rightZ, centreAlpha);
  let rightLength = Math.hypot(rightX, rightZ);
  if (rightLength <= 1e-9) {
    rightX = first.rightX;
    rightZ = first.rightZ;
    rightLength = Math.hypot(rightX, rightZ) || 1;
  }
  rightX /= rightLength;
  rightZ /= rightLength;
  const offsetX = x - centreX;
  const offsetZ = z - centreZ;
  result.distanceSq = offsetX * offsetX + offsetZ * offsetZ;
  result.signedDistance = offsetX * rightX + offsetZ * rightZ;
  // At the centreline, authored/sampled progress must remain exact. Toward the
  // road edge, the triangle's longitudinal barycentric coordinate takes over
  // so progress follows the actually rendered (skewed) ribbon.
  const halfWidth = Math.max(
    0.001,
    lerp(first.width, second.width, centreAlpha) * 0.5,
  );
  const ribbonBlend = clamp(Math.abs(result.signedDistance) / halfWidth, 0, 1);
  result.alpha = lerp(centreAlpha, clamp(triangle.alpha, 0, 1), ribbonBlend);
  result.surfaceY = triangle.surfaceY;
  // Adjacent ribbon quads share their longitudinal end caps. Counting those
  // internal seams as a road edge made every baked sample look like the edge
  // of the asphalt, which could activate shoulder recovery on the centreline.
  // Only the two authored lateral sides bound the driveable ribbon.
  const lateralEdgeDistance = Math.sqrt(Math.min(
    pointSegmentDistanceSq(x, z, left0X, left0Z, left1X, left1Z),
    pointSegmentDistanceSq(x, z, right0X, right0Z, right1X, right1Z),
  ));
  result.edgeDistance = triangle.inside
    ? -lateralEdgeDistance
    : Math.sqrt(triangle.distanceSq);

  // Outside the visible deck, continue its local crossfall smoothly into the
  // shoulder instead of pinning off-road terrain to the closest edge height.
  if (!triangle.inside) {
    const surfaceAlpha = clamp(triangle.alpha, 0, 1);
    let tangentX = lerp(first.tangentX, second.tangentX, surfaceAlpha);
    let tangentY = lerp(first.tangentY, second.tangentY, surfaceAlpha);
    let tangentZ = lerp(first.tangentZ, second.tangentZ, surfaceAlpha);
    const tangentLength = Math.hypot(tangentX, tangentY, tangentZ) || 1;
    tangentX /= tangentLength;
    tangentY /= tangentLength;
    tangentZ /= tangentLength;
    const rightY = Math.hypot(tangentX, tangentZ)
      * Math.sin(lerp(first.bank, second.bank, surfaceAlpha));
    const horizontalRightLength = Math.max(1e-6, Math.sqrt(Math.max(0, 1 - rightY * rightY)));
    const outsideOffset = Math.sqrt(triangle.distanceSq)
      * Math.sign(result.signedDistance || triangle.lateral || 1);
    result.surfaceY = triangle.surfaceY + outsideOffset * rightY / horizontalRightLength;
  }
}

function matchSurfaceTriangle(
  pointX: number,
  pointZ: number,
  ax: number,
  ay: number,
  az: number,
  aAlpha: number,
  aLateral: number,
  bx: number,
  by: number,
  bz: number,
  bAlpha: number,
  bLateral: number,
  cx: number,
  cy: number,
  cz: number,
  cAlpha: number,
  cLateral: number,
  result: TriangleSurfaceMatch,
): void {
  const v0x = bx - ax;
  const v0z = bz - az;
  const v1x = cx - ax;
  const v1z = cz - az;
  const v2x = pointX - ax;
  const v2z = pointZ - az;
  const d00 = v0x * v0x + v0z * v0z;
  const d01 = v0x * v1x + v0z * v1z;
  const d11 = v1x * v1x + v1z * v1z;
  const d20 = v2x * v0x + v2z * v0z;
  const d21 = v2x * v1x + v2z * v1z;
  const denominator = d00 * d11 - d01 * d01;
  if (Math.abs(denominator) > 1e-12) {
    const v = (d11 * d20 - d01 * d21) / denominator;
    const w = (d00 * d21 - d01 * d20) / denominator;
    const u = 1 - v - w;
    if (u >= -1e-9 && v >= -1e-9 && w >= -1e-9) {
      if (!result.inside) {
        result.distanceSq = 0;
        result.surfaceY = ay * u + by * v + cy * w;
        result.alpha = aAlpha * u + bAlpha * v + cAlpha * w;
        result.lateral = aLateral * u + bLateral * v + cLateral * w;
        result.inside = true;
      }
      return;
    }
  }
  if (result.inside) return;
  matchSurfaceEdge(
    pointX,
    pointZ,
    ax,
    ay,
    az,
    aAlpha,
    aLateral,
    bx,
    by,
    bz,
    bAlpha,
    bLateral,
    result,
  );
  matchSurfaceEdge(
    pointX,
    pointZ,
    bx,
    by,
    bz,
    bAlpha,
    bLateral,
    cx,
    cy,
    cz,
    cAlpha,
    cLateral,
    result,
  );
  matchSurfaceEdge(
    pointX,
    pointZ,
    cx,
    cy,
    cz,
    cAlpha,
    cLateral,
    ax,
    ay,
    az,
    aAlpha,
    aLateral,
    result,
  );
}

function matchSurfaceEdge(
  pointX: number,
  pointZ: number,
  ax: number,
  ay: number,
  az: number,
  aAlpha: number,
  aLateral: number,
  bx: number,
  by: number,
  bz: number,
  bAlpha: number,
  bLateral: number,
  result: TriangleSurfaceMatch,
): void {
  const dx = bx - ax;
  const dz = bz - az;
  const lengthSq = dx * dx + dz * dz;
  const alpha = lengthSq <= 1e-12
    ? 0
    : clamp(((pointX - ax) * dx + (pointZ - az) * dz) / lengthSq, 0, 1);
  const closestX = ax + dx * alpha;
  const closestZ = az + dz * alpha;
  const distanceSq = (pointX - closestX) ** 2 + (pointZ - closestZ) ** 2;
  if (distanceSq >= result.distanceSq) return;
  result.distanceSq = distanceSq;
  result.surfaceY = lerp(ay, by, alpha);
  result.alpha = lerp(aAlpha, bAlpha, alpha);
  result.lateral = lerp(aLateral, bLateral, alpha);
}

function pointSegmentDistanceSq(
  pointX: number,
  pointZ: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const dx = bx - ax;
  const dz = bz - az;
  const lengthSq = dx * dx + dz * dz;
  const alpha = lengthSq <= 1e-12
    ? 0
    : clamp(((pointX - ax) * dx + (pointZ - az) * dz) / lengthSq, 0, 1);
  const offsetX = pointX - (ax + dx * alpha);
  const offsetZ = pointZ - (az + dz * alpha);
  return offsetX * offsetX + offsetZ * offsetZ;
}

function exactProjectionFromBakedSegment(
  road: BakedRoadGeometry,
  segmentIndex: number,
  x: number,
  z: number,
): TrackProjection {
  const match: RibbonSurfaceMatch = {
    alpha: 0,
    distanceSq: Number.POSITIVE_INFINITY,
    signedDistance: 0,
    surfaceY: 0,
    edgeDistance: Number.POSITIVE_INFINITY,
  };
  const triangle: TriangleSurfaceMatch = {
    distanceSq: Number.POSITIVE_INFINITY,
    surfaceY: 0,
    alpha: 0,
    lateral: 0,
    inside: false,
  };
  bakedSegmentSurfaceMatch(road, segmentIndex, x, z, match, triangle);
  return projectionFromBakedSegment(
    road,
    segmentIndex,
    match.alpha,
    match.distanceSq,
    match.signedDistance,
    match.surfaceY,
    match.edgeDistance,
  );
}

function projectionFromBakedSegment(
  road: BakedRoadGeometry,
  segmentIndex: number,
  alpha: number,
  distanceSq: number,
  signedDistance: number,
  surfaceY?: number,
  roadEdgeDistance?: number,
): TrackProjection {
  const first = road.samples[segmentIndex] ?? road.samples[0] ?? emptyRoadSample();
  const nextIndex = road.closed ? (segmentIndex + 1) % road.samples.length : segmentIndex + 1;
  const second = road.samples[nextIndex] ?? first;
  const dx = second.x - first.x;
  const dz = second.z - first.z;
  const px = first.x + dx * alpha;
  const pz = first.z + dz * alpha;
  const segmentCount = road.closed ? road.samples.length : Math.max(1, road.samples.length - 1);
  const rawProgress = (segmentIndex + alpha) / segmentCount;
  const progress = road.closed ? mod(rawProgress, 1) : clamp(rawProgress, 0, 1);
  let secondParameter = second.parameter;
  if (road.closed && nextIndex === 0) secondParameter = 1;
  const rawParameter = lerp(first.parameter, secondParameter, alpha);
  const parameter = road.closed ? mod(rawParameter, 1) : clamp(rawParameter, 0, 1);
  const sourcePosition = parameter * road.sourceSegmentCount;
  const sourceFloor = Math.floor(sourcePosition);
  const segment = sourceSegmentForParameter(parameter, road.sourceSegmentCount, road.closed);
  let tangentX = lerp(first.tangentX, second.tangentX, alpha);
  let tangentY = lerp(first.tangentY, second.tangentY, alpha);
  let tangentZ = lerp(first.tangentZ, second.tangentZ, alpha);
  const tangentLength = Math.hypot(tangentX, tangentY, tangentZ) || 1;
  tangentX /= tangentLength;
  tangentY /= tangentLength;
  tangentZ /= tangentLength;
  const bank = lerp(first.bank, second.bank, alpha);
  let horizontalRightX = tangentZ;
  let horizontalRightZ = -tangentX;
  const horizontalLength = Math.hypot(horizontalRightX, horizontalRightZ);
  if (horizontalLength > 1e-9) {
    horizontalRightX /= horizontalLength;
    horizontalRightZ /= horizontalLength;
  } else {
    horizontalRightX = 1;
    horizontalRightZ = 0;
  }
  const cosine = Math.cos(bank);
  const sine = Math.sin(bank);
  let rightX = horizontalRightX * cosine + tangentY * horizontalRightZ * sine;
  let rightY = (tangentZ * horizontalRightX - tangentX * horizontalRightZ) * sine;
  let rightZ = horizontalRightZ * cosine - tangentY * horizontalRightX * sine;
  const rightLength = Math.hypot(rightX, rightY, rightZ) || 1;
  rightX /= rightLength;
  rightY /= rightLength;
  rightZ /= rightLength;
  let normalX = tangentY * rightZ - tangentZ * rightY;
  let normalY = tangentZ * rightX - tangentX * rightZ;
  let normalZ = tangentX * rightY - tangentY * rightX;
  const normalLength = Math.hypot(normalX, normalY, normalZ) || 1;
  normalX /= normalLength;
  normalY /= normalLength;
  normalZ /= normalLength;
  const horizontalRightLength = Math.max(1e-6, Math.hypot(rightX, rightZ));
  const exactSurfaceY = surfaceY ?? (
    lerp(first.y, second.y, alpha) + signedDistance * rightY / horizontalRightLength
  );
  const exactRoadEdgeDistance = roadEdgeDistance ?? (
    Math.abs(signedDistance) - lerp(first.width, second.width, alpha) * 0.5
  );
  return {
    x: px,
    y: lerp(first.y, second.y, alpha),
    z: pz,
    distance: Math.sqrt(distanceSq),
    segment,
    segmentT: road.closed && rawParameter >= 1 ? 0 : sourcePosition - sourceFloor,
    bakedSegment: segmentIndex,
    progress,
    roadWidth: lerp(first.width, second.width, alpha),
    heading: Math.atan2(tangentX, tangentZ),
    tangentX,
    tangentY,
    tangentZ,
    bank,
    rightX,
    rightY,
    rightZ,
    normalX,
    normalY,
    normalZ,
    signedDistance,
    surfaceY: exactSurfaceY,
    roadEdgeDistance: exactRoadEdgeDistance,
  };
}

function initialProjection(road: BakedRoadGeometry): TrackProjection {
  const first = road.samples[0] ?? emptyRoadSample();
  return {
    x: first.x,
    y: first.y,
    z: first.z,
    distance: Number.POSITIVE_INFINITY,
    segment: first.sourceSegment,
    segmentT: 0,
    bakedSegment: 0,
    progress: 0,
    roadWidth: first.width,
    heading: Math.atan2(first.tangentX, first.tangentZ),
    tangentX: first.tangentX,
    tangentY: first.tangentY,
    tangentZ: first.tangentZ,
    bank: first.bank,
    rightX: first.rightX,
    rightY: first.rightY,
    rightZ: first.rightZ,
    normalX: first.normalX,
    normalY: first.normalY,
    normalZ: first.normalZ,
    signedDistance: 0,
    surfaceY: first.y,
    roadEdgeDistance: -first.width * 0.5,
  };
}

function bakeRoad(
  points: readonly TrackPoint[],
  widths: readonly number[],
  closed: boolean,
  denseDivisions: number,
  minimumSamples: number,
  maximumSamples: number,
  adjustHeights?: (samples: MutableRoadSample[]) => void,
): BakedRoadGeometry {
  const sourceSegmentCount = closed ? points.length : Math.max(1, points.length - 1);
  const banks = points.map((point) => THREE_DEGREES * (point.bank ?? 0));
  const dense: DenseSplineSample[] = [];
  let totalLength = 0;
  let previous = evaluateCentripetalSpline(points, closed, 0);
  dense.push({ ...previous, parameter: 0, distance: 0 });
  for (let index = 1; index <= denseDivisions; index += 1) {
    const parameter = index / denseDivisions;
    const point = evaluateCentripetalSpline(points, closed, parameter);
    totalLength += Math.hypot(point.x - previous.x, point.y - previous.y, point.z - previous.z);
    dense.push({ ...point, parameter, distance: totalLength });
    previous = point;
  }

  const segmentCount = clamp(Math.ceil(totalLength * 1.45), minimumSamples, maximumSamples);
  const outputCount = closed ? segmentCount : segmentCount + 1;
  const samples: MutableRoadSample[] = [];
  for (let index = 0; index < outputCount; index += 1) {
    const progress = index / segmentCount;
    const targetDistance = progress * totalLength;
    const denseIndex = lowerBoundDistance(dense, targetDistance);
    const first = dense[Math.max(0, denseIndex - 1)] ?? dense[0]!;
    const second = dense[denseIndex] ?? first;
    const distanceSpan = second.distance - first.distance;
    const alpha = distanceSpan <= 1e-9 ? 0 : (targetDistance - first.distance) / distanceSpan;
    const parameter = lerp(first.parameter, second.parameter, alpha);
    samples.push({
      x: lerp(first.x, second.x, alpha),
      y: lerp(first.y, second.y, alpha),
      z: lerp(first.z, second.z, alpha),
      tangentX: 0,
      tangentY: 0,
      tangentZ: 1,
      bank: valueAtParameter(banks, parameter, closed),
      rightX: 1,
      rightY: 0,
      rightZ: 0,
      normalX: 0,
      normalY: 1,
      normalZ: 0,
      width: widthAtParameter(widths, parameter, closed),
      progress,
      parameter: closed ? mod(parameter, 1) : parameter,
      sourceSegment: sourceSegmentForParameter(parameter, sourceSegmentCount, closed),
    });
  }

  adjustHeights?.(samples);

  for (let index = 0; index < samples.length; index += 1) {
    const previousIndex = closed ? mod(index - 1, samples.length) : Math.max(0, index - 1);
    const nextIndex = closed ? mod(index + 1, samples.length) : Math.min(samples.length - 1, index + 1);
    const before = samples[previousIndex] ?? samples[index]!;
    const after = samples[nextIndex] ?? samples[index]!;
    const tangent = normalise3(after.x - before.x, after.y - before.y, after.z - before.z);
    samples[index]!.tangentX = tangent.x;
    samples[index]!.tangentY = tangent.y;
    samples[index]!.tangentZ = tangent.z;
    const frame = roadFrame(tangent.x, tangent.y, tangent.z, samples[index]!.bank);
    samples[index]!.rightX = frame.rightX;
    samples[index]!.rightY = frame.rightY;
    samples[index]!.rightZ = frame.rightZ;
    samples[index]!.normalX = frame.normalX;
    samples[index]!.normalY = frame.normalY;
    samples[index]!.normalZ = frame.normalZ;
  }

  const segmentIndicesBySource: number[][] = Array.from({ length: sourceSegmentCount }, () => []);
  const spatialCellSize = 18;
  const spatialSegments = new Map<number, number[]>();
  const bakedSegmentCount = closed ? samples.length : Math.max(0, samples.length - 1);
  const ribbonBounds = new Float32Array(bakedSegmentCount * 4);
  for (let index = 0; index < bakedSegmentCount; index += 1) {
    const first = samples[index]!;
    const next = samples[closed ? (index + 1) % samples.length : index + 1] ?? first;
    let nextParameter = next.parameter;
    if (closed && index === samples.length - 1) nextParameter = 1;
    const midpointParameter = (first.parameter + nextParameter) * 0.5;
    const source = sourceSegmentForParameter(midpointParameter, sourceSegmentCount, closed);
    segmentIndicesBySource[source]!.push(index);
    const firstHalfWidth = first.width * 0.5;
    const nextHalfWidth = next.width * 0.5;
    const left0X = first.x - first.rightX * firstHalfWidth;
    const right0X = first.x + first.rightX * firstHalfWidth;
    const left1X = next.x - next.rightX * nextHalfWidth;
    const right1X = next.x + next.rightX * nextHalfWidth;
    const left0Z = first.z - first.rightZ * firstHalfWidth;
    const right0Z = first.z + first.rightZ * firstHalfWidth;
    const left1Z = next.z - next.rightZ * nextHalfWidth;
    const right1Z = next.z + next.rightZ * nextHalfWidth;
    const minimumRibbonX = Math.min(left0X, right0X, left1X, right1X);
    const maximumRibbonX = Math.max(left0X, right0X, left1X, right1X);
    const minimumRibbonZ = Math.min(left0Z, right0Z, left1Z, right1Z);
    const maximumRibbonZ = Math.max(left0Z, right0Z, left1Z, right1Z);
    ribbonBounds[index * 4] = minimumRibbonX;
    ribbonBounds[index * 4 + 1] = maximumRibbonX;
    ribbonBounds[index * 4 + 2] = minimumRibbonZ;
    ribbonBounds[index * 4 + 3] = maximumRibbonZ;
    // Index the complete visible ribbon, not only its centreline. Wide
    // hairpins can otherwise hide the correct deck from nearby surface probes.
    const spatialMinimumX = Math.max(
      minimumRibbonX,
      Math.min(first.x, next.x) - MAX_SPATIAL_RIBBON_HALF_WIDTH,
    );
    const spatialMaximumX = Math.min(
      maximumRibbonX,
      Math.max(first.x, next.x) + MAX_SPATIAL_RIBBON_HALF_WIDTH,
    );
    const spatialMinimumZ = Math.max(
      minimumRibbonZ,
      Math.min(first.z, next.z) - MAX_SPATIAL_RIBBON_HALF_WIDTH,
    );
    const spatialMaximumZ = Math.min(
      maximumRibbonZ,
      Math.max(first.z, next.z) + MAX_SPATIAL_RIBBON_HALF_WIDTH,
    );
    const minCellX = Math.floor(
      (spatialMinimumX - ROAD_SPATIAL_QUERY_PADDING) / spatialCellSize,
    );
    const maxCellX = Math.floor(
      (spatialMaximumX + ROAD_SPATIAL_QUERY_PADDING) / spatialCellSize,
    );
    const minCellZ = Math.floor(
      (spatialMinimumZ - ROAD_SPATIAL_QUERY_PADDING) / spatialCellSize,
    );
    const maxCellZ = Math.floor(
      (spatialMaximumZ + ROAD_SPATIAL_QUERY_PADDING) / spatialCellSize,
    );
    for (let cellX = minCellX; cellX <= maxCellX; cellX += 1) {
      for (let cellZ = minCellZ; cellZ <= maxCellZ; cellZ += 1) {
        const key = spatialKey(cellX, cellZ);
        const bucket = spatialSegments.get(key) ?? [];
        bucket.push(index);
        spatialSegments.set(key, bucket);
      }
    }
  }

  return {
    closed,
    length: totalLength,
    samples,
    sourceSegmentCount,
    segmentIndicesBySource,
    spatialCellSize,
    spatialSegments,
    ribbonBounds,
    spatialMarks: new Uint32Array(bakedSegmentCount),
    spatialCandidates: [],
    spatialGeneration: 0,
  };
}

/** Verge (edge to edge) at which a shortcut runs at its own authored height;
 * closer than this it is eased onto the main road's surface. */
const ROUTE_CONFORM_VERGE = 10;
/** Height gaps at least this large are a genuine flyover, never conformed. */
const ROUTE_FLYOVER_CLEARANCE = 4.5;
/** Length over which a route eases between its own and the main profile. */
const ROUTE_CONFORM_EASE_METRES = 12;
/** Steepest grade a conformed shortcut may take (the map contract is 0.18). */
const ROUTE_MAX_GRADE = 0.17;

/**
 * Where a shortcut overlaps or runs right beside the main carriageway the two
 * ribbons must share one surface. Authored route points are sparse, so their
 * spline used to sag up to three metres under the main road's edge: a
 * hidden cliff that dropped cars through the seam. Heights within the fused
 * stretch follow the main surface (including its banking) and ease back to
 * the authored profile as the verge opens up.
 */
function conformRouteToMain(samples: MutableRoadSample[], main: BakedRoadGeometry): void {
  if (samples.length < 3) return;
  const targets = samples.map((sample) => {
    let best = Number.POSITIVE_INFINITY;
    let nearest: BakedRoadSample | null = null;
    for (const candidate of main.samples) {
      const distance = (candidate.x - sample.x) ** 2 + (candidate.z - sample.z) ** 2;
      if (distance < best) {
        best = distance;
        nearest = candidate;
      }
    }
    if (!nearest) return { weight: 0, y: sample.y };
    const lateral = (sample.x - nearest.x) * nearest.rightX + (sample.z - nearest.z) * nearest.rightZ;
    const verge = Math.abs(lateral) - nearest.width * 0.5 - sample.width * 0.5;
    const clampedLateral = clamp(lateral, -nearest.width * 0.5, nearest.width * 0.5);
    const surfaceY = nearest.y + nearest.rightY * clampedLateral;
    if (Math.abs(surfaceY - sample.y) >= ROUTE_FLYOVER_CLEARANCE) return { weight: 0, y: sample.y };
    const t = clamp(1 - verge / ROUTE_CONFORM_VERGE, 0, 1);
    return { weight: t * t * (3 - 2 * t), y: surfaceY };
  });
  const conformed = samples.map((sample, index) => lerp(sample.y, targets[index]!.y, targets[index]!.weight));
  // Ease the blend along the route so the grade change spreads over metres,
  // not one sample; both junction ends stay pinned to the main road.
  const spacing = Math.max(0.1, Math.hypot(
    samples[1]!.x - samples[0]!.x,
    samples[1]!.y - samples[0]!.y,
    samples[1]!.z - samples[0]!.z,
  ));
  const radius = Math.max(2, Math.round(ROUTE_CONFORM_EASE_METRES / spacing));
  const original = samples.map((sample) => sample.y);
  const influences = samples.map(() => 0);
  for (let index = 1; index < samples.length - 1; index += 1) {
    let sum = 0;
    let count = 0;
    let influence = 0;
    for (let offset = -radius; offset <= radius; offset += 1) {
      const neighbour = clamp(index + offset, 0, samples.length - 1);
      sum += conformed[neighbour]!;
      count += 1;
      influence = Math.max(influence, targets[neighbour]!.weight);
    }
    // Smoothing only shapes the transition (an authored flyover far from any
    // fused stretch keeps its exact profile) and a fused stretch sits exactly
    // on the main surface.
    influences[index] = influence;
    const smoothed = lerp(original[index]!, sum / count, influence);
    samples[index]!.y = lerp(smoothed, targets[index]!.y, targets[index]!.weight);
  }
  // A shortcut that cuts the inside of a climbing bend reaches the main
  // road's heights over less distance than the main road does. Keep its own
  // grade drivable: the forward/backward passes bound every step while the
  // pinned junction ends hold the profile to the main road. Authored stretches
  // away from the main road (a deliberate flyover ramp) are left alone.
  const maximumRise = ROUTE_MAX_GRADE * spacing;
  for (let pass = 0; pass < 4; pass += 1) {
    for (let index = 1; index < samples.length - 1; index += 1) {
      if (influences[index] === 0) continue;
      const previous = samples[index - 1]!.y;
      samples[index]!.y = clamp(samples[index]!.y, previous - maximumRise, previous + maximumRise);
    }
    for (let index = samples.length - 2; index > 0; index -= 1) {
      if (influences[index] === 0) continue;
      const next = samples[index + 1]!.y;
      samples[index]!.y = clamp(samples[index]!.y, next - maximumRise, next + maximumRise);
    }
  }
}

/** Exact centripetal Catmull-Rom evaluation, independent of Three.js. */
function evaluateCentripetalSpline(
  points: readonly TrackPoint[],
  closed: boolean,
  parameterValue: number,
): { x: number; y: number; z: number } {
  if (points.length === 0) return { x: 0, y: 0, z: 0 };
  if (points.length === 1) return point3(points[0]!);
  const parameter = closed ? mod(parameterValue, 1) : clamp(parameterValue, 0, 1);
  const scaled = (points.length - (closed ? 0 : 1)) * parameter;
  let pointIndex = Math.floor(scaled);
  let weight = scaled - pointIndex;
  if (!closed && weight === 0 && pointIndex === points.length - 1) {
    pointIndex = points.length - 2;
    weight = 1;
  }

  const first = point3(points[closed ? mod(pointIndex - 1, points.length) : Math.max(0, pointIndex - 1)]!);
  const second = point3(points[closed ? mod(pointIndex, points.length) : pointIndex]!);
  const third = point3(points[closed ? mod(pointIndex + 1, points.length) : pointIndex + 1]!);
  const fourth = point3(points[closed ? mod(pointIndex + 2, points.length) : Math.min(points.length - 1, pointIndex + 2)]!);

  if (!closed && pointIndex === 0) {
    first.x = second.x * 2 - third.x;
    first.y = second.y * 2 - third.y;
    first.z = second.z * 2 - third.z;
  }
  if (!closed && pointIndex + 2 >= points.length) {
    fourth.x = third.x * 2 - second.x;
    fourth.y = third.y * 2 - second.y;
    fourth.z = third.z * 2 - second.z;
  }

  let dt0 = Math.pow(distanceSquared(first, second), 0.25);
  let dt1 = Math.pow(distanceSquared(second, third), 0.25);
  let dt2 = Math.pow(distanceSquared(third, fourth), 0.25);
  if (dt1 < 1e-4) dt1 = 1;
  if (dt0 < 1e-4) dt0 = dt1;
  if (dt2 < 1e-4) dt2 = dt1;
  return {
    x: nonUniformCatmull(first.x, second.x, third.x, fourth.x, dt0, dt1, dt2, weight),
    y: nonUniformCatmull(first.y, second.y, third.y, fourth.y, dt0, dt1, dt2, weight),
    z: nonUniformCatmull(first.z, second.z, third.z, fourth.z, dt0, dt1, dt2, weight),
  };
}

function nonUniformCatmull(
  p0: number,
  p1: number,
  p2: number,
  p3: number,
  dt0: number,
  dt1: number,
  dt2: number,
  t: number,
): number {
  let tangent1 = (p1 - p0) / dt0 - (p2 - p0) / (dt0 + dt1) + (p2 - p1) / dt1;
  let tangent2 = (p2 - p1) / dt1 - (p3 - p1) / (dt1 + dt2) + (p3 - p2) / dt2;
  tangent1 *= dt1;
  tangent2 *= dt1;
  const c0 = p1;
  const c1 = tangent1;
  const c2 = -3 * p1 + 3 * p2 - 2 * tangent1 - tangent2;
  const c3 = 2 * p1 - 2 * p2 + tangent1 + tangent2;
  return c0 + c1 * t + c2 * t * t + c3 * t * t * t;
}

function parameterToArcProgress(parameterValue: number, road: BakedRoadGeometry): number {
  const parameter = road.closed ? mod(parameterValue, 1) : clamp(parameterValue, 0, 1);
  const samples = road.samples;
  if (samples.length < 2) return 0;
  let previous = samples[0]!;
  for (let index = 1; index < samples.length; index += 1) {
    const next = samples[index]!;
    if (next.parameter + 1e-9 < parameter) {
      previous = next;
      continue;
    }
    const span = next.parameter - previous.parameter;
    const alpha = span <= 1e-9 ? 0 : (parameter - previous.parameter) / span;
    return lerp(previous.progress, next.progress, alpha);
  }
  if (road.closed) {
    const span = 1 - previous.parameter;
    const alpha = span <= 1e-9 ? 0 : (parameter - previous.parameter) / span;
    return lerp(previous.progress, 1, alpha);
  }
  return 1;
}

function lowerBoundDistance(samples: readonly DenseSplineSample[], target: number): number {
  let low = 0;
  let high = samples.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) * 0.5);
    if ((samples[middle]?.distance ?? 0) < target) low = middle + 1;
    else high = middle;
  }
  return low;
}

function nearbyBakedSegments(
  road: BakedRoadGeometry,
  x: number,
  z: number,
): readonly number[] {
  road.spatialGeneration = (road.spatialGeneration + 1) >>> 0;
  if (road.spatialGeneration === 0) {
    road.spatialMarks.fill(0);
    road.spatialGeneration = 1;
  }
  const generation = road.spatialGeneration;
  const candidates = road.spatialCandidates;
  candidates.length = 0;
  const cellX = Math.floor(x / road.spatialCellSize);
  const cellZ = Math.floor(z / road.spatialCellSize);
  for (const segment of road.spatialSegments.get(spatialKey(cellX, cellZ)) ?? []) {
    if (road.spatialMarks[segment] === generation) continue;
    road.spatialMarks[segment] = generation;
    candidates.push(segment);
  }
  return candidates;
}

// 2^20 cells of 18 m each way covers ±18 900 km; the packed key stays far
// below Number.MAX_SAFE_INTEGER.
const SPATIAL_KEY_OFFSET = 0x10_0000;
const SPATIAL_KEY_STRIDE = 0x20_0000;

/** Packs a signed grid cell into one exact integer key. Numeric Map keys
 * avoid allocating and hashing a template string on every road projection. */
function spatialKey(x: number, z: number): number {
  return (x + SPATIAL_KEY_OFFSET) * SPATIAL_KEY_STRIDE + (z + SPATIAL_KEY_OFFSET);
}

function widthAtParameter(widths: readonly number[], parameterValue: number, closed: boolean): number {
  return valueAtParameter(widths, parameterValue, closed);
}

function valueAtParameter(values: readonly number[], parameterValue: number, closed: boolean): number {
  if (values.length === 0) return 0;
  if (values.length === 1) return values[0]!;
  const parameter = closed ? mod(parameterValue, 1) : clamp(parameterValue, 0, 1);
  const segmentCount = closed ? values.length : values.length - 1;
  const scaled = parameter * segmentCount;
  const index = Math.min(segmentCount - 1, Math.floor(scaled));
  const alpha = parameter >= 1 ? 1 : scaled - index;
  const nextIndex = closed ? (index + 1) % values.length : index + 1;
  return lerp(values[index] ?? values[0]!, values[nextIndex] ?? values[index]!, alpha);
}

function sourceSegmentForParameter(parameterValue: number, sourceSegmentCount: number, closed: boolean): number {
  if (sourceSegmentCount <= 0) return 0;
  const parameter = closed ? mod(parameterValue, 1) : clamp(parameterValue, 0, 1);
  return Math.min(sourceSegmentCount - 1, Math.floor(parameter * sourceSegmentCount));
}

function point3(point: TrackPoint): { x: number; y: number; z: number } {
  return { x: point.x, y: point.y ?? 0, z: point.z };
}

function distanceSquared(
  first: { x: number; y: number; z: number },
  second: { x: number; y: number; z: number },
): number {
  return (second.x - first.x) ** 2 + (second.y - first.y) ** 2 + (second.z - first.z) ** 2;
}

function normalise2(x: number, z: number, fallbackX: number, fallbackZ: number): { x: number; z: number } {
  const length = Math.hypot(x, z);
  if (length > 1e-9) return { x: x / length, z: z / length };
  const fallbackLength = Math.hypot(fallbackX, fallbackZ) || 1;
  return { x: fallbackX / fallbackLength, z: fallbackZ / fallbackLength };
}

function normalise3(x: number, y: number, z: number): { x: number; y: number; z: number } {
  const length = Math.hypot(x, y, z) || 1;
  return { x: x / length, y: y / length, z: z / length };
}

const THREE_DEGREES = Math.PI / 180;
const COPLANAR_RIBBON_TOLERANCE = 0.6;

function roadFrame(tangentX: number, tangentY: number, tangentZ: number, bank: number): {
  rightX: number;
  rightY: number;
  rightZ: number;
  normalX: number;
  normalY: number;
  normalZ: number;
} {
  const horizontalRight = normalise2(tangentZ, -tangentX, 1, 0);
  const cosine = Math.cos(bank);
  const sine = Math.sin(bank);
  // Rodrigues rotation of the horizontal right vector around the tangent.
  const crossX = tangentY * horizontalRight.z;
  const crossY = tangentZ * horizontalRight.x - tangentX * horizontalRight.z;
  const crossZ = -tangentY * horizontalRight.x;
  const right = normalise3(
    horizontalRight.x * cosine + crossX * sine,
    crossY * sine,
    horizontalRight.z * cosine + crossZ * sine,
  );
  const normal = normalise3(
    tangentY * right.z - tangentZ * right.y,
    tangentZ * right.x - tangentX * right.z,
    tangentX * right.y - tangentY * right.x,
  );
  return {
    rightX: right.x,
    rightY: right.y,
    rightZ: right.z,
    normalX: normal.x,
    normalY: normal.y,
    normalZ: normal.z,
  };
}

function emptyRoadSample(): BakedRoadSample {
  return {
    x: 0,
    y: 0,
    z: 0,
    tangentX: 0,
    tangentY: 0,
    tangentZ: 1,
    bank: 0,
    rightX: 1,
    rightY: 0,
    rightZ: 0,
    normalX: 0,
    normalY: 1,
    normalZ: 0,
    width: 0,
    progress: 0,
    parameter: 0,
    sourceSegment: 0,
  };
}

/**
 * Track definitions keep their compact design widths while every runtime
 * system receives a road that is at least twice as wide. Corners gain an
 * additional smooth bonus so hairpins do not become the narrowest sections.
 */
export function roadWidthAtTrackPoint(map: MapDefinition, index: number): number {
  const point = map.track[mod(index, map.track.length)];
  if (!point) return map.roadWidth * ROAD_WIDTH_MULTIPLIER;
  const authoredWidth = (point.width ?? map.roadWidth) * ROAD_WIDTH_MULTIPLIER;
  const curveWidth = map.roadWidth
    * ROAD_WIDTH_MULTIPLIER
    * (1 + trackCurvatureAt(map.track, index) * MAX_CURVE_WIDTH_BONUS);
  return Math.max(authoredWidth, curveWidth);
}

/** Normalised 0..1 corner severity at a route point. */
export function trackCurvatureAt(track: TrackPoint[], index: number): number {
  if (track.length < 3) return 0;
  const previous = track[mod(index - 1, track.length)];
  const current = track[mod(index, track.length)];
  const next = track[mod(index + 1, track.length)];
  if (!previous || !current || !next) return 0;
  const incoming = Math.atan2(current.x - previous.x, current.z - previous.z);
  const outgoing = Math.atan2(next.x - current.x, next.z - current.z);
  const turn = Math.abs(signedAngleDifference(outgoing, incoming));
  const normalised = clamp((turn - 0.1) / 1.45, 0, 1);
  return normalised * normalised * (3 - 2 * normalised);
}

/**
 * Signed metres between a point and the nearest triangulated asphalt edge.
 * Supplying a height ignores genuinely separated flyovers while still
 * detecting furniture that cuts through another road at vehicle height.
 */
export function roadEdgeClearance(
  map: MapDefinition,
  x: number,
  z: number,
  referenceY?: number,
): number {
  const geometry = getTrackGeometry(map);
  let clearance = roadRibbonEdgeClearance(geometry.main, x, z, referenceY);
  for (const road of geometry.alternateRoutes.values()) {
    clearance = Math.min(
      clearance,
      roadRibbonEdgeClearance(road, x, z, referenceY, clearance),
    );
  }
  return clearance;
}

/** Edge clearance to the alternate-route ribbons only (ignores the main
 * road). Divider rails use it to find the verge between the main carriageway
 * and a shortcut running alongside it. */
export function alternateRouteEdgeClearance(
  map: MapDefinition,
  x: number,
  z: number,
  referenceY?: number,
): number {
  const geometry = getTrackGeometry(map);
  let clearance = Number.POSITIVE_INFINITY;
  for (const road of geometry.alternateRoutes.values()) {
    clearance = Math.min(clearance, roadRibbonEdgeClearance(road, x, z, referenceY, clearance));
  }
  return clearance;
}

/**
 * Edge clearance to every drivable ribbon except one route. A route's own
 * guardrail must not be rejected by the route's own road edge.
 */
export function roadEdgeClearanceExcept(
  map: MapDefinition,
  excludeRouteId: string | null,
  x: number,
  z: number,
  referenceY?: number,
): number {
  const geometry = getTrackGeometry(map);
  let clearance = roadRibbonEdgeClearance(geometry.main, x, z, referenceY);
  for (const [routeId, road] of geometry.alternateRoutes) {
    if (routeId === excludeRouteId) continue;
    clearance = Math.min(clearance, roadRibbonEdgeClearance(road, x, z, referenceY, clearance));
  }
  return clearance;
}

function roadRibbonEdgeClearance(
  road: BakedRoadGeometry,
  x: number,
  z: number,
  referenceY?: number,
  maximumRelevantDistance = Number.POSITIVE_INFINITY,
): number {
  const segmentCount = road.closed ? road.samples.length : Math.max(0, road.samples.length - 1);
  if (segmentCount === 0) return Number.POSITIVE_INFINITY;
  let best = Number.POSITIVE_INFINITY;
  let matched = false;
  const match: RibbonSurfaceMatch = {
    alpha: 0,
    distanceSq: Number.POSITIVE_INFINITY,
    signedDistance: 0,
    surfaceY: 0,
    edgeDistance: Number.POSITIVE_INFINITY,
  };
  const triangle: TriangleSurfaceMatch = {
    distanceSq: Number.POSITIVE_INFINITY,
    surfaceY: 0,
    alpha: 0,
    lateral: 0,
    inside: false,
  };
  const consider = (segment: number): void => {
    if (Number.isFinite(best) && best >= 0) {
      const boundsIndex = segment * 4;
      const minimumX = road.ribbonBounds[boundsIndex] ?? 0;
      const maximumX = road.ribbonBounds[boundsIndex + 1] ?? minimumX;
      const minimumZ = road.ribbonBounds[boundsIndex + 2] ?? 0;
      const maximumZ = road.ribbonBounds[boundsIndex + 3] ?? minimumZ;
      const boundsOffsetX = x < minimumX ? minimumX - x : x > maximumX ? x - maximumX : 0;
      const boundsOffsetZ = z < minimumZ ? minimumZ - z : z > maximumZ ? z - maximumZ : 0;
      if (boundsOffsetX * boundsOffsetX + boundsOffsetZ * boundsOffsetZ > best * best) return;
    }
    bakedSegmentSurfaceMatch(road, segment, x, z, match, triangle);
    if (Number.isFinite(referenceY) && Math.abs(match.surfaceY - Number(referenceY)) >= 3.4) return;
    matched = true;
    best = Math.min(best, match.edgeDistance);
  };
  const nearby = nearbyBakedSegments(road, x, z);
  for (const segment of nearby) {
    consider(segment);
    if (best < 0) return best;
  }
  if (matched) return best;
  // The spatial index includes four metres beyond the complete ribbon. If a
  // closer road was already found, an empty cell proves this road cannot win
  // and avoids a full alternate-route scan for every guardrail probe.
  if (maximumRelevantDistance <= ROAD_SPATIAL_QUERY_PADDING) return Number.POSITIVE_INFINITY;
  for (let segment = 0; segment < segmentCount; segment += 1) {
    consider(segment);
    if (best < 0) return best;
  }
  return best;
}

/**
 * Centre of the guardrail that belongs to a particular point of the main
 * route. Keeping this in shared code lets rendering and collision authority
 * agree about places where another strip of asphalt crosses that rail.
 */
export function guardrailPointAt(
  map: MapDefinition,
  progress: number,
  side: -1 | 1,
): GuardrailPoint {
  const sample = sampleTrackAt(map, progress);
  const offset = sample.width * 0.5 + GUARDRAIL_OFFSET;
  return {
    x: sample.x + sample.rightX * offset * side,
    y: sample.y + sample.rightY * offset * side,
    z: sample.z + sample.rightZ * offset * side,
    side,
    progress: mod(progress, 1),
  };
}

/** Smooth spline heading retained for callers that only own the point list. */
export function trackHeading(track: TrackPoint[], index: number): number {
  if (track.length < 2) return 0;
  const parameter = mod(index, track.length) / track.length;
  const before = evaluateCentripetalSpline(track, true, parameter - 0.0001);
  const after = evaluateCentripetalSpline(track, true, parameter + 0.0001);
  return Math.atan2(after.x - before.x, after.z - before.z);
}

export function signedAngleDifference(target: number, current: number): number {
  let difference = target - current;
  while (difference > Math.PI) difference -= Math.PI * 2;
  while (difference < -Math.PI) difference += Math.PI * 2;
  return difference;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function lerp(from: number, to: number, alpha: number): number {
  return from + (to - from) * alpha;
}

export function mod(value: number, modulus: number): number {
  return ((value % modulus) + modulus) % modulus;
}
